import _ from 'lodash'
import io, { Manager } from 'socket.io-client'
import wildcard from 'socketio-wildcard'
import { eventChannel } from 'redux-saga'
import { all, call, put, select, spawn, take } from 'redux-saga/effects'
import getConfig from 'next/config'

import { getJWT } from 'common/utils'

import { fireTestEventFromSocket } from './actions'
import eventMap from './event-map'

const { publicRuntimeConfig } = getConfig() as { publicRuntimeConfig: { WS_URL?: string } }

interface SocketEvent {
  type: string
  payload: unknown
}

function createSocketChannel(socket: ReturnType<typeof io>): ReturnType<typeof eventChannel> {
  return eventChannel((emit) => {
    const eventHandler = (event: { data: [string, unknown] }) => {
      const {
        data: [type, payload],
      } = event
      emit({ type, payload })
    }

    const errorHandler = (err: Error & { reason?: string }) => {
      emit(new Error(err.reason ?? err.message))
    }

    socket.on('*', eventHandler)
    socket.on('connect_error', errorHandler)

    const unsubscribe = () => {
      socket.off('*', eventHandler)
    }

    return unsubscribe
  })
}

let socketHandlingInitialzied = false

export function* handleSocketEvents(): Generator<unknown, void, unknown> {
  if (typeof window === 'undefined' || socketHandlingInitialzied) {
    return
  }

  const jwt = getJWT()
  const options: { transports: string[]; query?: Record<string, string> } = {
    transports: ['websocket', 'polling'],
  }
  if (jwt !== undefined) {
    options.query = { auth_token: jwt }
  }

  const socketUrl = publicRuntimeConfig.WS_URL
  if (socketUrl === undefined) {
    return
  }
  const socket = io(socketUrl, options)

  wildcard(Manager)(socket)
  const socketChannel = (yield call(createSocketChannel, socket)) as ReturnType<
    typeof createSocketChannel
  >

  socketHandlingInitialzied = true
  while (true) {
    try {
      const event = (yield take(socketChannel)) as SocketEvent
      const actions = (yield select(
        (state: {
          socketCommunication: { listeners: Array<{ eventType: string; actionType: string }> }
        }) => _.filter(state.socketCommunication.listeners, { eventType: event.type }),
      )) as Array<{ eventType: string; actionType: string }>
      const transformData = _.get(
        _.find(eventMap, { eventType: event.type }),
        'transformData',
        _.identity,
      ) as (data: unknown, eventKey: string) => unknown
      yield all(
        _.map(actions, (action) =>
          put({ type: action.actionType, payload: transformData(event.payload, event.type) }),
        ),
      )
      yield put(fireTestEventFromSocket(event))
    } catch {
      // socketChannel still open in catch block
    }
  }
}

export default function* saga(): Generator<unknown, void, unknown> {
  yield all([spawn(handleSocketEvents)])
}
